import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useList } from '@refinedev/core';
import { BatchScreen } from '@viq-admin/screens';
import type {
  AggregationsResponse,
  BatchConference,
  BatchCrossTenantSalesforceApi,
  BatchCustomTagDefinition,
  BatchExportApi,
  BatchLead,
  BatchLeadFieldsApi,
  BatchLeadNotesApi,
  BatchLeadsPage,
  BatchMoveApi,
  BatchPersonTag,
  BatchSalesforceCompany,
  BatchSalesforceExportResult,
  BatchSalesforceMember,
  BatchTagsApi,
} from '@viq-admin/screens';
import { superadminFetch, superadminFetchRaw } from '../../api/dataProvider';
import { listLeads } from '../../api/leads';
import { listCompanyConferences, moveLeads } from '../../api/leadMove';
import { patchLeadFields } from '../../api/leadFields';
import { createRawnote, editRawnote } from '../../api/leadEdit';
import { getScopeCompanyId } from '../../scope/companyScope';
import { useCompanyScope } from '../../components/CompanyScopePicker';
import { useAbility } from '../../ability/AbilityContext';
import { LeadDetailBody } from '../Leads/LeadDetailBody';

interface ConfRow {
  id: string;
  name: string;
  location?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

// Scope-aware fetcher factories. The active "operate as tenant" scope (or null
// for All tenants) is baked into the closure so the fetcher REFERENCE changes
// when the scope changes. That matters because the Batch table's first-page
// load effect keys on the `fetchLeads` identity: a new ref re-runs it under the
// new scope (the shared BatchTable holds leads in local state, not a query, so
// invalidateQueries() alone wouldn't reload it). The aggregations useQuery is
// additionally refetched by the provider's global invalidateQueries().
//
// These read getScopeCompanyId() at call time; the `scopeCompanyId` arg only
// gates the closure identity (it always mirrors the module scope). Explicit
// companyId is never passed here, so the scope always applies (the Salesforce
// dialog's explicit-companyId fetchers live in `salesforceApi`, untouched).
const makeFetchAggregations =
  (_scopeCompanyId: string | null) =>
  (conferenceId: string): Promise<AggregationsResponse> => {
    const scope = getScopeCompanyId();
    const qs = scope ? `?companyId=${encodeURIComponent(scope)}` : '';
    return superadminFetch<AggregationsResponse>(
      `/v1/superadmin/conferences/${conferenceId}/aggregations${qs}`,
    );
  };

const makeFetchLeads =
  (_scopeCompanyId: string | null) =>
  (args: {
    conferenceId: string;
    cursor?: string;
    limit?: number;
    deleted?: boolean;
  }): Promise<BatchLeadsPage> =>
    listLeads({
      conferenceId: args.conferenceId,
      cursor: args.cursor,
      limit: args.limit,
      deleted: args.deleted,
      companyId: getScopeCompanyId() ?? undefined,
    });

/**
 * Editable tag + assignment API wired to the cross-tenant super-admin plane.
 * POSTing a custom tag auto-creates its definition server-side; there is no
 * "create definition" endpoint (the picker offers existing definitions + free
 * custom values). Mutations act on a leadId the backend resolves to its tenant.
 */
const tagsApi: BatchTagsApi = {
  fetchTagDefinitions: () =>
    superadminFetch<{ definitions: BatchCustomTagDefinition[] }>(
      '/v1/superadmin/tag-definitions',
    ),
  addTag: (leadId, body) =>
    superadminFetch<{ tags: BatchPersonTag[] }>(
      `/v1/superadmin/leads/${leadId}/tags`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  removeTag: (leadId, category, value) =>
    superadminFetch<{ tags: BatchPersonTag[] }>(
      `/v1/superadmin/leads/${leadId}/tags/${encodeURIComponent(category)}/${encodeURIComponent(value)}`,
      { method: 'DELETE' },
    ),
  setAssignment: (leadId, assignedTo) =>
    superadminFetch<{ assignedTo: string | null }>(
      `/v1/superadmin/leads/${leadId}/assignment`,
      { method: 'PATCH', body: JSON.stringify({ assignedTo }) },
    ),
};

/**
 * Reads a filename from the `Content-Disposition` header, falling back to a
 * generic name when the server didn't set one. Mirrors the portal's helper.
 */
function readFilename(response: Response, fallback: string): string {
  const cd = response.headers.get('Content-Disposition') ?? '';
  const match = cd.match(/filename="([^"]+)"/);
  return match?.[1] ?? fallback;
}

/**
 * Export API for the picked company scope. Discovery params are sent ONLY
 * when a company is scoped: the backend accepts them exactly then, and
 * rejects them (422) on an unscoped cross-tenant export — where there is no
 * single question catalog for them to govern. Scope=null therefore produces
 * byte-identical requests to the pre-parity console.
 */
function makeExportApi(companyId: string | null): BatchExportApi {
  return {
    downloadExport: async ({
      conferenceId, mode, format, hideColumns, includeDiscovery, includeDiscoverySummary,
    }) => {
      const params = new URLSearchParams({ conferenceId, mode, format });
      // Appended ONLY when non-default, so an untouched dialog produces
      // byte-identical params to the pre-column-picker request. CSV-only — the
      // backend 422s it on the JSON branch.
      if (hideColumns?.length) params.set('hideColumns', hideColumns.join(','));
      if (companyId) {
        params.set('companyId', companyId);
        // Appended only when non-default, mirroring hideColumns.
        if (includeDiscovery === false) params.set('includeDiscovery', 'false');
        if (includeDiscoverySummary === false) params.set('includeDiscoverySummary', 'false');
      }
      const res = await superadminFetchRaw(
        `/v1/superadmin/leads/export?${params.toString()}`,
      );
      const blob = await res.blob();
      const filename = readFilename(res, `leads_export.${format}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // On failure superadminFetchRaw throws an HttpError; the shared ExportDialog
      // catches it and shows a message.error toast (single-toast, portal-faithful —
      // the portal's downloadExport helper likewise throws without toasting).
    },
    markExported: async (conferenceId) => {
      await superadminFetch(
        `/v1/superadmin/conferences/${conferenceId}/export/mark`,
        { method: 'POST', body: '{}' },
      );
    },
    // `superadminFetch` sends `Content-Type: application/json` via authHeaders(),
    // so the PUT needs no explicit header of its own.
    getColumnPrefs: () => superadminFetch('/v1/superadmin/export-preferences'),
    saveColumnPrefs: (prefs) =>
      superadminFetch('/v1/superadmin/export-preferences', {
        method: 'PUT',
        body: JSON.stringify(prefs),
      }),
  };
}

/** CRM connection row (subset) from `/v1/superadmin/crm/connections`. */
interface CrmConnectionRow {
  companyId: string;
  companyName: string | null;
  crmSource: string;
}

// Fetch a generous single page of the target company's leads for selection.
// The dialog is a one-shot picker, not a cursor-paginated table.
const SF_LEAD_LIMIT = 500;

/**
 * Cross-tenant Salesforce push API wired to the super-admin plane. Unlike the
 * customer portal (which targets the caller's own company), the console picks a
 * TARGET company: `listSalesforceCompanies` surfaces only SF-connected tenants,
 * and every subsequent call carries that company's id so the backend routes the
 * write to that tenant's own Salesforce org. The push itself is gated by the
 * dialog's type-`CONFIRM` modal before it reaches this call.
 */
const salesforceApi: BatchCrossTenantSalesforceApi = {
  listSalesforceCompanies: async (): Promise<BatchSalesforceCompany[]> => {
    const { connections } = await superadminFetch<{
      connections: CrmConnectionRow[];
    }>('/v1/superadmin/crm/connections');
    return connections
      .filter((c) => c.crmSource === 'salesforce')
      .map((c) => ({ companyId: c.companyId, companyName: c.companyName ?? c.companyId }));
  },
  fetchMembers: (conferenceId, companyId): Promise<BatchSalesforceMember[]> =>
    superadminFetch<{ members: BatchSalesforceMember[] }>(
      `/v1/superadmin/conferences/${conferenceId}/salesforce/members?companyId=${encodeURIComponent(companyId)}`,
    ).then((r) => r.members),
  fetchCompanyLeads: (conferenceId, companyId): Promise<BatchLead[]> =>
    listLeads({ conferenceId, companyId, limit: SF_LEAD_LIMIT }).then(
      (page) => page.leads as unknown as BatchLead[],
    ),
  exportToSalesforce: (
    conferenceId,
    { targetCompanyId, personIds, mode },
  ): Promise<BatchSalesforceExportResult> =>
    superadminFetch<BatchSalesforceExportResult>(
      `/v1/superadmin/conferences/${conferenceId}/export/salesforce`,
      {
        method: 'POST',
        body: JSON.stringify({ targetCompanyId, personIds, mode }),
      },
    ),
};

/**
 * Console Batch route. `/v1/superadmin/conferences` returns ACTIVE conferences
 * across ALL tenants, so the super-admin event picker is inherently
 * cross-tenant. Renders the shared Batch workspace wired to the super-admin
 * plane's aggregations + leads + tag/assignment endpoints, with the shared
 * LeadDetailBody in the drawer. Inline editing of the grid's contact fields
 * (`leadFieldsApi`) follows `update Lead`, NOT `readOnly`: the read-only role
 * may edit a lead's fields while its tags and assignment stay read-only. So
 * does editing the grid's Notes column (`leadNotesApi`).
 */
export function BatchRoute() {
  const { companyId } = useCompanyScope();
  const readOnly = !useAbility().can('manage', 'Lead');
  const canUpdateLead = useAbility().can('update', 'Lead');
  // Rebuild the fetchers when the tenant scope changes so the shared BatchTable
  // (which keys its load effect on the fetcher identity) reloads under the new
  // scope. Conferences refetch via the provider's global invalidateQueries().
  const fetchAggregations = useMemo(
    () => makeFetchAggregations(companyId),
    [companyId],
  );
  const fetchLeads = useMemo(() => makeFetchLeads(companyId), [companyId]);
  const exportApi = useMemo(() => makeExportApi(companyId), [companyId]);

  // Inline editing of the grid's contact fields (viq-admin#57), for anyone
  // with `update Lead` — independent of `readOnly` (spec D10), which keeps
  // governing tags and assignment. Built per render rather than on the
  // module-level `tagsApi`, so the ability decides it. The route is keyed by
  // lead id only, so the company scope doesn't enter the closure. UX only —
  // `requireAbility('update', 'Lead')` on the route is the real gate.
  const leadFieldsApi = useMemo<BatchLeadFieldsApi | undefined>(
    () => (canUpdateLead ? { updateFields: patchLeadFields } : undefined),
    [canUpdateLead],
  );

  // The grid's Notes column (port plan step 5), over the note endpoints the
  // lead drawer already uses: POST /v1/superadmin/leads/:id/notes for a lead
  // with no note, PATCH …/notes/:noteId with the shown note's `serverVersion`
  // as `expectedVersion` otherwise (409 when someone else saved first). Keyed
  // by lead id only, like the field edit, so the company scope doesn't enter
  // the closure. For `update Lead`, built per render like `leadFieldsApi`.
  // A create's `createdAt` is the row's `updated_at` (the insert stamps both
  // with one now(); the mapped response keeps only `updatedAt`); an edit
  // leaves `created_at` alone. UX only — `requireAbility('update', 'Lead')`
  // on both routes is the real gate.
  const leadNotesApi = useMemo<BatchLeadNotesApi | undefined>(
    () =>
      canUpdateLead
        ? {
            createNote: async (leadId, text) => {
              const saved = await createRawnote(leadId, text);
              return { id: saved.id, text: saved.text, serverVersion: saved.serverVersion, createdAt: saved.updatedAt };
            },
            editNote: async (leadId, note, text) => {
              const saved = await editRawnote(leadId, note.id, { text }, note.serverVersion);
              return { id: saved.id, text: saved.text, serverVersion: saved.serverVersion, createdAt: note.createdAt };
            },
          }
        : undefined,
    [canUpdateLead],
  );

  // Move-to-conference action, scoped to the operator's currently selected
  // company. `companyId` is passed through as-is (including null for "All
  // tenants") so BatchScreen can disable + explain the affordance rather than
  // hide it outright. The destination list's company scoping is enforced
  // server-side (INNER JOIN on company_conferences) — this route does no
  // client-side filtering of its own.
  const moveApi = useMemo<BatchMoveApi>(
    () => ({
      companyId,
      listDestinations: listCompanyConferences,
      moveLeads,
    }),
    [companyId],
  );

  // Fail-closed Salesforce gate: the footer button (and its dead-end dialog)
  // should only appear when at least one tenant has Salesforce connected.
  // Shares the SalesforceExportDialog's own `['salesforceCompanies']` query key
  // so the dialog reuses this cache instead of firing a second request.
  const { data: sfCompanies } = useQuery({
    queryKey: ['salesforceCompanies'],
    queryFn: () => salesforceApi.listSalesforceCompanies(),
  });
  const gatedSalesforceApi = useMemo<BatchCrossTenantSalesforceApi>(
    () => ({ ...salesforceApi, connected: (sfCompanies?.length ?? 0) > 0 }),
    [sfCompanies],
  );

  const { result, query } = useList<ConfRow>({ resource: 'conferences' });
  const conferences = useMemo<BatchConference[]>(
    () =>
      (result?.data ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        location: c.location ?? null,
        startDate: c.startDate ?? undefined,
        endDate: c.endDate ?? undefined,
      })),
    [result?.data],
  );
  return (
    <BatchScreen
      conferences={conferences}
      conferencesLoading={query.isLoading}
      fetchAggregations={fetchAggregations}
      fetchLeads={fetchLeads}
      tagsApi={tagsApi}
      exportApi={exportApi}
      // Discovery columns exist only within a single tenant's question
      // catalog: the switches (and the params behind them) are offered only
      // once a company is scoped, matching the backend's 422 on the
      // unscoped path — see makeExportApi above.
      showDiscovery={Boolean(companyId)}
      exportNote={companyId ? undefined : 'To include discovery answers, choose a company instead of All tenants.'}
      salesforceApi={readOnly ? undefined : gatedSalesforceApi}
      moveApi={moveApi}
      readOnly={readOnly}
      leadFieldsApi={leadFieldsApi}
      leadNotesApi={leadNotesApi}
      renderLeadDetail={(id) => <LeadDetailBody id={id} />}
    />
  );
}
